import React, { useState, useEffect, useContext, useCallback } from "react"
import { db, arrayUnion, auth } from "../../../utils/firebase"
import { AuthContext } from "../../../utils/providers/auth"
import firebase from "firebase"
import moment from "moment"
import { NavLink } from "react-router-dom"

// UI components
import Button from "../../../components/ui/button/button"
import ViewArchivedEnquiry from "../../../components/enquiry/view/archived-view"

// Functional component to return archived enquiries that can be reopened
export default function Archive(props) {
	const { searchEmail, sort, onSearchingChange } = props
	const [enquiries, setEnquiries] = useState([])
	const [clientsById, setClientsById] = useState({})
	const [usersById, setUsersById] = useState({})
	const [reopening, setReopening] = useState(null)
	const [viewingArchivedEnquiry, setViewingArchivedEnquiry] = useState(null)
	const [currentArchivedDocument, setCurrentArchivedDocument] = useState(null)
	const [searching, setSearching] = useState(false)
	const [hasSearched, setHasSearched] = useState(false)

	// Pull the current user's object in from the auth context
	const { user } = useContext(AuthContext)

	// Check if user can see all archived enquiries (admins & managers)
	const canViewAllArchived = user?.show_for?.can_assign || user?.is_admin || user?.is_manager

	// Notify parent of searching state changes
	useEffect(() => {
		if (onSearchingChange) {
			onSearchingChange(searching)
		}
	}, [searching, onSearchingChange])

	// Search for archived enquiries by email
	const searchByEmail = useCallback(async (email) => {
		if (!email || email.trim().length < 4) {
			setEnquiries([])
			setHasSearched(false)
			return
		}

		setSearching(true)
		setHasSearched(true)

		try {
			const searchTerm = email.toLowerCase().trim()

			// Step 1: Search clients collection for matching emails
			// Firestore range queries are case-sensitive, so we query multiple case variants
			// to handle emails stored with different casing (e.g. "Liam..." vs "liam...")
			const lowerTerm = searchTerm
			const upperTerm = searchTerm.charAt(0).toUpperCase() + searchTerm.slice(1)

			const [lowerSnapshot, upperSnapshot] = await Promise.all([
				db.collection("clients")
					.where("email", ">=", lowerTerm)
					.where("email", "<=", lowerTerm + "\uf8ff")
					.limit(50)
					.get(),
				db.collection("clients")
					.where("email", ">=", upperTerm)
					.where("email", "<=", upperTerm + "\uf8ff")
					.limit(50)
					.get(),
			])

			// Merge results from both queries, deduplicating by document ID
			const clientMap = new Map()
			lowerSnapshot.forEach((doc) => clientMap.set(doc.id, doc))
			upperSnapshot.forEach((doc) => clientMap.set(doc.id, doc))

			if (clientMap.size === 0) {
				setEnquiries([])
				setSearching(false)
				return
			}

			// Step 2: Build array of client IDs from matching clients
			const matchingClientIds = Array.from(clientMap.keys())

			// Step 3: Query enquiries_removed for these client IDs
			// Firestore 'in' query limit is 10, so we need to batch
			const batchSize = 10
			const archivedEnquiries = []
			const nextClientsById = {}
			clientMap.forEach((doc, id) => {
				nextClientsById[id] = doc.data()
			})

			for (let i = 0; i < matchingClientIds.length; i += batchSize) {
				const batch = matchingClientIds.slice(i, i + batchSize)
				
				let query = db
					.collection("enquiries_removed")
					.where("enquiry.client", "in", batch)
					.orderBy("created", "desc")

				// If not admin/manager, filter by current user's agent ID
				if (!canViewAllArchived) {
					query = query.where("agent", "==", auth.currentUser.uid)
				}

				const snapshot = await query.get()
				snapshot.forEach((doc) => {
					archivedEnquiries.push({
						id: doc.id,
						...doc.data(),
					})
				})
			}

			// Sort all results by created date descending
			archivedEnquiries.sort((a, b) => (b.created?.seconds || 0) - (a.created?.seconds || 0))

			const agentIds = Array.from(new Set(archivedEnquiries.map((enquiry) => enquiry.agent).filter(Boolean)))
			const nextUsersById = {}
			for (let i = 0; i < agentIds.length; i += batchSize) {
				const batch = agentIds.slice(i, i + batchSize)
				const userDocs = await db
					.collection("users")
					.where(firebase.firestore.FieldPath.documentId(), "in", batch)
					.get()
				userDocs.forEach((doc) => {
					nextUsersById[doc.id] = doc.data()
				})
			}

			setClientsById(nextClientsById)
			setUsersById(nextUsersById)
			setEnquiries(archivedEnquiries)
		} catch (error) {
			console.error("Error searching archived enquiries:", error)
			setEnquiries([])
			setClientsById({})
			setUsersById({})
		}

		setSearching(false)
	}, [canViewAllArchived])

	// Debounce search when email changes
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			searchByEmail(searchEmail)
		}, 300) // 300ms debounce

		return () => clearTimeout(timeoutId)
	}, [searchEmail, searchByEmail])

	// View an archived enquiry using the data stored in the enquiry field
	const viewArchivedEnquiry = (archivedEnquiry) => {
		// Set the archived enquiry data to view
		setViewingArchivedEnquiry(archivedEnquiry.enquiry)
		setCurrentArchivedDocument(archivedEnquiry)
	}

	// Reopen an archived enquiry
	const reopenEnquiry = async (archivedEnquiry) => {
		setReopening(archivedEnquiry.id)

		try {
			const { enquiry: enquiryData } = archivedEnquiry

			const restoredPayload = {
				...enquiryData,
				// Keep the original created date to maintain proper enquiry age
				created: enquiryData.created,
				status: "BEING_PROCESSED",
				restored: true,
				restored_at: firebase.firestore.FieldValue.serverTimestamp(),
				original_removal_date: archivedEnquiry.created,
			}

			// Restore the enquiry to the main enquiries collection. Deleting an
			// enquiry never deletes its subcollections, so its quotes (drafts, sent
			// and cancelled) and activity are still sitting under the ORIGINAL
			// enquiry ID - restoring onto that same ID (stamped onto the archive doc
			// as `enquiry_id`) reattaches them all. Older archive docs predate that
			// stamp, so they fall back to a fresh document.
			let restoredID = archivedEnquiry.enquiry_id || ""
			if (restoredID) {
				await db.doc(`enquiries/${restoredID}`).set(restoredPayload)
			} else {
				const restoredRef = await db.collection("enquiries").add(restoredPayload)
				restoredID = restoredRef.id
			}

			// Add the enquiry back to the client's enquiries array if it has a client
			if (enquiryData.client) {
				await db.doc(`clients/${enquiryData.client}`).set(
					{
						enquiries: arrayUnion(restoredID),
					},
					{ merge: true }
				)
			}

			// Remove from archived collection
			await db.doc(`enquiries_removed/${archivedEnquiry.id}`).delete()

			// Update local state to remove the reopened enquiry
			setEnquiries((prevEnquiries) => prevEnquiries.filter((enq) => enq.id !== archivedEnquiry.id))
		} catch (error) {
			console.error("Error reopening enquiry:", error)
		}

		setReopening(null)
	}

	// Archive enquiry row component
	const ArchiveRow = ({ enquiry }) => {
		const { site, agent, created, enquiry: baseEnquiry } = enquiry
		const client = clientsById[baseEnquiry.client] || {}
		const archiveUser = usersById[agent] || {}
		const clientName = `${client.first_name || ""} ${client.last_name || ""}`.trim()
		const agentName = `${archiveUser.first_name || ""} ${archiveUser.last_name || ""}`.trim()

		// Format dates
		const archivedOn = moment(created?.seconds, "X").format("MMM D[,] YYYY")
		const dateFrom = moment(baseEnquiry.date_from?.seconds, "X").format("MMM D[,] YYYY")
		const followUp = baseEnquiry.follow_up ? moment(baseEnquiry.follow_up?.seconds, "X").format("MMM D[,] YYYY") : "No follow up"

		// Calculate age when archived
		const ageWhenArchived = baseEnquiry.created ? moment(created?.seconds, "X").diff(moment(baseEnquiry.created?.seconds, "X"), "days") : 0

		return (
			<tr>
				<td>{site}</td>
				<td>{baseEnquiry.client ? <NavLink to={`client/${baseEnquiry.client}`}>{clientName || "Loading..."}</NavLink> : <small className="no-value">No client</small>}</td>
				<td>{baseEnquiry?.location || <small className="no-value">-</small>}</td>
				<td>{dateFrom}</td>
				<td>
					{followUp === "No follow up" && <small>{followUp}</small>}
					{followUp !== "No follow up" && <>{followUp}</>}
				</td>
				<td>{baseEnquiry?.group_size || <small className="no-value">-</small>}</td>
				<td>£{baseEnquiry?.quoted || 0}</td>
				<td>
					{ageWhenArchived} {ageWhenArchived === 1 ? "day" : "days"}
				</td>
				<td>{agentName || "Unknown"}</td>
				<td>{archivedOn}</td>
				<td
					className="is-button"
					style={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
					<div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", width: "fit-content" }}>
						<Button
							small={true}
							label="View enquiry"
							onClick={() => viewArchivedEnquiry(enquiry)}
						/>
						<Button
							small={true}
							label={reopening === enquiry.id ? "Reopening..." : "Reopen Enquiry"}
							disabled={reopening === enquiry.id}
							onClick={() => reopenEnquiry(enquiry)}
						/>
					</div>
				</td>
			</tr>
		)
	}

	const getSeconds = (timestamp) => timestamp?._seconds || timestamp?.seconds || 0
	const compareValues = (a, b) => {
		if (typeof a === "number" || typeof b === "number") return (a || 0) - (b || 0)
		return String(a || "").localeCompare(String(b || ""), undefined, { numeric: true, sensitivity: "base" })
	}
	const getSortValue = (archivedEnquiry, key) => {
		const baseEnquiry = archivedEnquiry.enquiry || {}
		const client = clientsById[baseEnquiry.client] || {}
		const archiveUser = usersById[archivedEnquiry.agent] || {}
		switch (key) {
			case "site":
				return archivedEnquiry.site
			case "name":
				return `${client.first_name || ""} ${client.last_name || ""}`.trim()
			case "location":
				return baseEnquiry.location
			case "date_from":
				return getSeconds(baseEnquiry.date_from)
			case "follow_up":
				return getSeconds(baseEnquiry.follow_up)
			case "group_size":
				return baseEnquiry.group_size
			case "quoted":
				return baseEnquiry.quoted
			case "age":
				return getSeconds(archivedEnquiry.created) - getSeconds(baseEnquiry.created)
			case "agent":
				return `${archiveUser.first_name || ""} ${archiveUser.last_name || ""}`.trim()
			case "archived_on":
				return getSeconds(archivedEnquiry.created)
			default:
				return ""
		}
	}
	const sortedEnquiries = sort?.key && sort?.direction
		? [...enquiries].sort((a, b) => {
			const comparison = compareValues(getSortValue(a, sort.key), getSortValue(b, sort.key))
			return sort.direction === "asc" ? comparison : -comparison
		})
		: [...enquiries].sort((a, b) => getSeconds(b.created) - getSeconds(a.created))

	// Show prompt to enter email if no search term
	if (!searchEmail || searchEmail.trim().length < 4) {
		return (
			<>
				<tr>
					<td colSpan="11">
						<small>{searchEmail && searchEmail.trim().length > 0 
							? `Please enter at least 4 characters to search (${searchEmail.trim().length}/4)` 
							: "Enter a customer email address above to search archived enquiries"}</small>
					</td>
				</tr>
			</>
		)
	}

	if (searching) {
		return (
			<>
				<tr>
					<td colSpan="11">Searching for archived enquiries...</td>
				</tr>
			</>
		)
	}

	if (enquiries.length === 0 && hasSearched) {
		return (
			<>
				<tr>
					<td colSpan="11">
						<small>No archived enquiries found for "{searchEmail}"</small>
					</td>
				</tr>
			</>
		)
	}

	return (
		<>
			{sortedEnquiries.map((enquiry) => (
				<ArchiveRow
					key={enquiry.id}
					enquiry={enquiry}
				/>
			))}

			{/* View archived enquiry window */}
			{viewingArchivedEnquiry && (
				<ViewArchivedEnquiry
					enquiryData={viewingArchivedEnquiry}
					close={() => {
						setViewingArchivedEnquiry(null)
						setCurrentArchivedDocument(null)
					}}
					onReopen={() => reopenEnquiry(currentArchivedDocument)}
				/>
			)}
		</>
	)
}
